import { useToast } from "@/hooks/use-toast";
import { useCallback } from "react";

export interface PushNotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  data?: any;
  actions?: NotificationAction[];
  tag?: string;
  requireInteraction?: boolean;
}

export interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

class PushNotificationService {
  private registration: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;
  private vapidPublicKey: string | null = null;
  private initializationAttempted: boolean = false;

  // Initialize the service worker and push notifications
  async initialize(): Promise<boolean> {
    // Prevent multiple initialization attempts
    if (this.initializationAttempted) {
      return this.registration !== null;
    }
    
    this.initializationAttempted = true;
    
    try {
      // Check if service worker is supported
      if (!('serviceWorker' in navigator)) {
        return false;
      }

      // Check if push messaging is supported
      if (!('PushManager' in window)) {
        return false;
      }

      // In development mode, check if SW exists before registering
      let isDev = false;
      try {
        isDev = import.meta.env.DEV;
      } catch (e) {
        // Fallback for older browsers - assume production
        isDev = false;
      }
      
      if (isDev) {
        try {
          const response = await fetch('/sw.js', { method: 'HEAD' });
          if (!response.ok || response.headers.get('content-type')?.includes('text/html')) {
            return false;
          }
        } catch (error) {
          return false;
        }
      }

      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js');

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      // Get VAPID public key from server
      await this.fetchVapidKey();

      return true;
    } catch (error) {
      return false;
    }
  }

  // Fetch VAPID public key from server
  private async fetchVapidKey(): Promise<void> {
    try {
      const response = await fetch('/api/push/vapid-key');
      if (response.ok) {
        const data = await response.json();
        this.vapidPublicKey = data.publicKey;
      }
    } catch (error) {
      // Silently fail - VAPID key is optional
    }
  }

  // Request notification permission
  async requestPermission(): Promise<NotificationPermission> {
    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      return 'denied';
    }
  }

  // Subscribe to push notifications
  async subscribe(): Promise<PushSubscription | null> {
    try {
      if (!this.registration || !this.vapidPublicKey) {
        return null;
      }

      // Check if already subscribed
      this.subscription = await this.registration.pushManager.getSubscription();
      if (this.subscription) {
        return this.subscription;
      }

      // Subscribe to push notifications
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey),
      });


      // Send subscription to server
      await this.sendSubscriptionToServer(this.subscription);

      return this.subscription;
    } catch (error) {
      return null;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe(): Promise<boolean> {
    try {
      if (!this.subscription) {
        return true;
      }

      await this.subscription.unsubscribe();

      // Remove subscription from server
      await this.removeSubscriptionFromServer(this.subscription);

      this.subscription = null;
      return true;
    } catch (error) {
      return false;
    }
  }

  // Send subscription to server
  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    try {
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send subscription to server');
      }
    } catch (error) {
      throw error;
    }
  }

  // Remove subscription from server
  private async removeSubscriptionFromServer(subscription: PushSubscription): Promise<void> {
    try {
      const response = await fetch('/api/push/unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove subscription from server');
      }
    } catch (error) {
      throw error;
    }
  }

  // Check if subscribed (validates both local and server state)
  async isSubscribed(): Promise<boolean> {
    try {
      if (!this.registration) {
        return false;
      }

      // Check local subscription first
      this.subscription = await this.registration.pushManager.getSubscription();
      if (!this.subscription) {
        return false;
      }

      // Verify subscription exists on server
      try {
        const response = await fetch('/api/push/subscriptions', {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          return false;
        }

        const data = await response.json();
        const subscriptions = data.subscriptions || [];
        
        // Check if current subscription endpoint exists in server subscriptions
        const currentEndpoint = this.subscription.endpoint;
        const serverHasSubscription = subscriptions.some((sub: any) => sub.endpoint === currentEndpoint);
        
        if (!serverHasSubscription) {
          // Local subscription exists but server doesn't have it - clean up local
          await this.subscription.unsubscribe();
          this.subscription = null;
          return false;
        }

        return true;
      } catch (serverError) {
        // If server check fails, fall back to local subscription status
        return !!this.subscription;
      }
    } catch (error) {
      return false;
    }
  }

  // Get current subscription
  async getSubscription(): Promise<PushSubscription | null> {
    try {
      if (!this.registration) {
        return null;
      }

      this.subscription = await this.registration.pushManager.getSubscription();
      return this.subscription;
    } catch (error) {
      return null;
    }
  }

  // Show local notification (fallback)
  showLocalNotification(payload: PushNotificationPayload): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(payload.title, {
        body: payload.body,
        icon: payload.icon || '/jam-jar.png',
        badge: payload.badge || '/jam-jar.png',
        data: payload.data,
        tag: payload.tag,
        requireInteraction: payload.requireInteraction,
      });
    }
  }

  // Utility function to convert VAPID key
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// Create singleton instance
export const pushNotificationService = new PushNotificationService();

// React hook for push notifications
export function usePushNotifications() {
  const { toast } = useToast();

  const initialize = useCallback(async () => {
    const success = await pushNotificationService.initialize();
    let isDev = false;
    try {
      isDev = import.meta.env.DEV;
    } catch (e) {
      // Fallback for older browsers - assume production
      isDev = false;
    }
    
    if (!success && !isDev) {
      toast({
        title: "Push Notifications Unavailable",
        description: "Your browser doesn't support push notifications.",
        variant: "destructive",
      });
    }
    return success;
  }, [toast]);

  const requestPermission = useCallback(async () => {
    const permission = await pushNotificationService.requestPermission();
    
    if (permission === 'granted') {
      toast({
        title: "Notifications Enabled",
        description: "You'll receive notifications for important events.",
      });
    } else if (permission === 'denied') {
      toast({
        title: "Notifications Blocked",
        description: "Please enable notifications in your browser settings.",
        variant: "destructive",
      });
    }
    
    return permission;
  }, [toast]);

  const subscribe = useCallback(async () => {
    try {
      const subscription = await pushNotificationService.subscribe();
      if (subscription) {
        toast({
          title: "Subscribed to Notifications",
          description: "You'll receive updates about your events.",
        });
      }
      return subscription;
    } catch (error) {
      toast({
        title: "Subscription Failed",
        description: "Failed to subscribe to notifications. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  }, [toast]);

  const unsubscribe = useCallback(async () => {
    try {
      const success = await pushNotificationService.unsubscribe();
      if (success) {
        toast({
          title: "Unsubscribed",
          description: "You won't receive notifications anymore.",
        });
      }
      return success;
    } catch (error) {
      toast({
        title: "Unsubscription Failed",
        description: "Failed to unsubscribe from notifications.",
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  const isSubscribed = useCallback(() => {
    return pushNotificationService.isSubscribed();
  }, []);

  const getSubscription = useCallback(() => {
    return pushNotificationService.getSubscription();
  }, []);

  const showLocalNotification = useCallback((payload: PushNotificationPayload) => {
    return pushNotificationService.showLocalNotification(payload);
  }, []);

  return {
    initialize,
    requestPermission,
    subscribe,
    unsubscribe,
    isSubscribed,
    getSubscription,
    showLocalNotification,
  };
}